// src/components/NewListModal.js
import React, { useEffect } from 'react';
import { FiX } from 'react-icons/fi';

const ToggleCheckboxGroup = ({ title, options, selected, onChange }) => {
    const handleSelect = (option) => {
        const newSelected = selected.includes(option)
            ? selected.filter(item => item !== option)
            : [...selected, option];
        onChange(newSelected);
    };

    const formatLabel = (text) => {
        if (!text) return '';
        if (text === 'skillLevel') return 'Skill Level';
        if (text === 'joinedDate') return 'Joined Date';
        if (text === 'createdAt') return 'Created Date';
        if (text === 'assignedTo') return 'Assigned To';
        return text.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    };

    return (
        <div className="form-group">
            <label className="form-label">{title}</label>
            <div className="toggle-group-grid">
                {options && options.length > 0 ? (
                    options.map(option => {
                        const uniqueId = `toggle-${title.replace(/\s+/g, '-')}-${option}`;
                        return (
                            <label htmlFor={uniqueId} key={option} className="toggle-group-item">
                                <span>{formatLabel(option)}</span>
                                <div className="toggle-switch">
                                    <input id={uniqueId} type="checkbox" checked={selected.includes(option)} onChange={() => handleSelect(option)} />
                                    <span className="slider"></span>
                                </div>
                            </label>
                        );
                    })
                ) : (
                    <div className="toggle-group-empty">
                        No filter options available.
                    </div>
                )}
            </div>
        </div>
    );
};


const NewListModal = ({
    isOpen,
    onClose,
    onAddList,
    listType,
    columnOptions,
    filterGroups,
    initialConfig = null // ✅ NEW: Accept initial config for editing
}) => {
    const generateInitialState = () => {
        // ✅ NEW: If editing, use the passed config
        if (initialConfig) {
            return {
                title: initialConfig.title,
                columns: initialConfig.columns || [],
                filters: initialConfig.filters || {}
            };
        }

        // Default state for creating a NEW list
        const initialFilters = {};
        if (filterGroups) {
            filterGroups.forEach(group => {
                initialFilters[group.key] = group.key === 'status' && group.options.length > 0 ? [group.options[0]] : [];
            });
        }

        return {
            title: `New ${listType} List`,
            columns: columnOptions ? columnOptions.slice(0, 4).map(c => c.accessor) : [],
            filters: initialFilters,
        };
    };

    const [config, setConfig] = React.useState(generateInitialState());

    // ✅ UPDATED: Reset state when opening or when initialConfig changes
    React.useEffect(() => {
        if (isOpen) {
            setConfig(generateInitialState());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialConfig, listType]);

    // Close on Escape Key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                e.preventDefault();
                onClose();
            }
        };
        if (isOpen) window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleConfigChange = (key, value) => {
        setConfig(prev => ({ ...prev, [key]: value }));
    };

    const handleFilterChange = (filterType, value) => {
        setConfig(prev => ({ ...prev, filters: { ...prev.filters, [filterType]: value } }));
    };

    const handleSubmit = () => {
        onAddList(config);
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content modal-content-wide">
                <div className="modal-header">
                    {/* ✅ UPDATED: Dynamic Title */}
                    <h3>{initialConfig ? `Edit ${listType} List` : `Add New ${listType} List`}</h3>
                    <button onClick={onClose} className="modal-close-btn"><FiX /></button>
                </div>
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <div className="form-group">
                        <label htmlFor="listTitle" className="form-label">List Title</label>
                        <input
                            id="listTitle"
                            type="text"
                            className="form-input"
                            value={config.title}
                            onChange={e => handleConfigChange('title', e.target.value)}
                        />
                    </div>
                    <hr className="section-divider" />
                    <ToggleCheckboxGroup
                        title="Columns to Display"
                        options={(columnOptions || []).map(c => c.accessor)}
                        selected={config.columns}
                        onChange={(value) => handleConfigChange('columns', value)}
                    />
                    <hr className="section-divider" />
                    <h3 className="form-label" style={{ marginBottom: '1rem' }}>Filters</h3>
                    {(filterGroups || []).map(group => (
                        <ToggleCheckboxGroup
                            key={group.key}
                            title={group.title}
                            options={group.options}
                            selected={config.filters[group.key] || []}
                            onChange={(value) => handleFilterChange(group.key, value)}
                        />
                    ))}
                </div>
                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    {/* ✅ UPDATED: Dynamic Button Text */}
                    <button type="button" className="btn btn-primary" onClick={handleSubmit}>
                        {initialConfig ? 'Save Changes' : 'Add List'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewListModal;